import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { BILL_TODO_TYPE_LABELS, BILL_TODO_TYPE_ORDER } from '../../../types/enums.ts';
import { cn } from '../../../lib/utils.ts';
import type { BillTodoType } from '../../../generated/enums.ts';
import type { BillTodo } from '../types/billTodo.ts';

type BillTodoBannersProps = {
    todos: BillTodo[];
    onDismiss: (todoType: BillTodoType) => Promise<void>;
    onSetTransitorySchedule?: () => void;
    onChangeInvoiceDate?: () => void;
    onShowDuplicates: () => void;
    className?: string;
};

type TodoCopy = {
    message: string;
    actionLabel: string;
};

const TODO_COPY: Record<BillTodoType, TodoCopy> = {
    transitory_schedule: {
        message: 'Groter dan €500 met een gescande start- en einddatum. Transitorisch?',
        actionLabel: 'Transitorische post instellen',
    },
    before_bookkeeping_start: {
        message: 'De factuurdatum ligt vóór de startdatum van de boekhouding.',
        actionLabel: 'Factuurdatum aanpassen',
    },
    possible_duplicate: {
        message: 'Een ander document heeft dezelfde relatie en hetzelfde factuurnummer.',
        actionLabel: 'Bekijk duplicaten',
    },
};

const LINK_CLASS =
    'shrink-0 whitespace-nowrap rounded font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 disabled:opacity-50';

export function BillTodoBanners({
    todos,
    onDismiss,
    onSetTransitorySchedule,
    onChangeInvoiceDate,
    onShowDuplicates,
    className,
}: BillTodoBannersProps) {
    const [dismissing, setDismissing] = useState<BillTodoType | null>(null);

    if (todos.length === 0) {
        return null;
    }

    const actions: Record<BillTodoType, (() => void) | undefined> = {
        transitory_schedule: onSetTransitorySchedule,
        before_bookkeeping_start: onChangeInvoiceDate,
        possible_duplicate: onShowDuplicates,
    };

    const ordered = [...todos].sort(
        (left, right) => BILL_TODO_TYPE_ORDER.indexOf(left.type) - BILL_TODO_TYPE_ORDER.indexOf(right.type),
    );

    const dismiss = async (todoType: BillTodoType) => {
        setDismissing(todoType);
        try {
            await onDismiss(todoType);
        } finally {
            setDismissing(null);
        }
    };

    return (
        <div
            role="group"
            aria-label="Taken voor dit document"
            className={cn(
                'pointer-events-auto divide-y divide-amber-100 rounded-md border border-amber-200 bg-amber-50 text-[12.5px] text-amber-900',
                className,
            )}
        >
            {ordered.map((todo) => {
                const action = actions[todo.type];
                return (
                    <div
                        key={todo.id}
                        data-testid={`bill-todo-banner-${todo.type}`}
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <InformationCircleIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                        <p className="min-w-0 flex-1">
                            <span className="font-semibold">{BILL_TODO_TYPE_LABELS[todo.type]}</span>
                            <span> · {TODO_COPY[todo.type].message}</span>
                        </p>
                        {action && (
                            <button
                                type="button"
                                onClick={action}
                                aria-haspopup={todo.type === 'possible_duplicate' ? 'dialog' : undefined}
                                className={LINK_CLASS}
                            >
                                {TODO_COPY[todo.type].actionLabel}
                            </button>
                        )}
                        {todo.dismissible && (
                            <button
                                type="button"
                                onClick={() => void dismiss(todo.type)}
                                disabled={dismissing !== null}
                                aria-busy={dismissing === todo.type}
                                aria-label={`Negeren: ${BILL_TODO_TYPE_LABELS[todo.type]}`}
                                className={LINK_CLASS}
                            >
                                Negeren
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
